import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import countryNames from '../data/countryNames.json';

/** Filtered country picker: type to narrow (e.g. "I…" → India, Indonesia, …). */
export default function CountryCombobox({ id, label, required, value, onChange, error }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || '');
  const wrapRef = useRef(null);

  useEffect(() => {
    setQ(value || '');
  }, [value]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return countryNames.slice(0, 18);
    return countryNames.filter((n) => n.toLowerCase().startsWith(needle)).slice(0, 80);
  }, [q]);

  const pick = useCallback(
    (name) => {
      onChange(name);
      setQ(name);
      setOpen(false);
    },
    [onChange],
  );

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const showError = !!error || (required && q && !countryNames.includes(q));

  return (
    <div className={`field ${showError ? 'field-has-error' : ''}`} ref={wrapRef}>
      {label && (
        <label htmlFor={id}>
          {label}
          {required ? ' *' : ''}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          autoComplete="off"
          required={required}
          value={q}
          onChange={(e) => {
            const v = e.target.value;
            setQ(v);
            onChange(v);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Type to search (e.g. Ind…)"
          aria-expanded={open}
          aria-controls={id ? `${id}-country-list` : undefined}
          aria-autocomplete="list"
          style={
            showError
              ? { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(220,38,38,0.12)' }
              : undefined
          }
        />
        {open && filtered.length > 0 && (
          <ul
            id={id ? `${id}-country-list` : undefined}
            role="listbox"
            className="country-combobox-list"
          >
            {filtered.map((name) => (
              <li key={name}>
                <button type="button" role="option" className="country-combobox-opt" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(name)}>
                  {name}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <span className="field-error-msg">{error}</span>}
      {required && q && !countryNames.includes(q) && !error && (
        <span className="field-error-msg">Pick a country from the suggestions or spell it exactly.</span>
      )}
    </div>
  );
}
